import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, LogOut, Home, Calendar, BarChart3, Package, Settings, User, Globe, Menu, X, DollarSign, ClipboardList, FlaskConical, Syringe } from 'lucide-react';
import PatientsExpanded from './PatientsExpanded';
import PatientForm from './PatientForm';
import SiteEditor from '../components/SiteEditor';
import PatientDetail from './PatientDetail';
import GoogleCalendarView from '../components/GoogleCalendarView';
import AppointmentsManager from '../components/AppointmentsManager';
import BillingTabs from '../components/BillingTabs';
import Catalogs from './Catalogs';
import NursingPanel from './NursingPanel';
import LabPanel from './LabPanel';
import ReportsHub from './Reports/ReportsHub';
import SamplesHub from './SamplesHub';
import UserManager from '../components/UserManager';
import SampleAppointments from '../components/SampleAppointments';
import SettingsPage from '../components/Settings';
import { API_URL } from '../config/api';
import './Dashboard.css';
import './DashboardResponsive.css';

const TodayPatients = ({ onPatientSelect }) => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchTodayAppointments();
    }, []);

    const fetchTodayAppointments = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const userData = localStorage.getItem('user');
            const currentUser = userData ? JSON.parse(userData) : null;
            const roleName = (currentUser?.role?.name || '').toLowerCase();
            const isDoctor = ['medico', 'doctor', 'médico'].includes(roleName);

            // Format today's date as YYYY-MM-DD in local timezone
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

            // 1. Fetch local appointments
            let localAppointments = [];
            try {
                const localRes = await fetch(`${API_URL}/appointments/today`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (localRes.ok) {
                    const localData = await localRes.json();
                    localAppointments = (localData.appointments || []).map(a => ({
                        ...a,
                        source: 'local'
                    }));
                }
            } catch (e) {
                console.error('Error fetching local appointments:', e);
            }

            // 2. Fetch Google Calendar events for today
            let googleAppointments = [];
            try {
                // Get list of doctors to fetch from
                const doctorsRes = await fetch(`${API_URL}/doctors`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (doctorsRes.ok) {
                    const doctorsData = await doctorsRes.json();
                    let doctorsList = Array.isArray(doctorsData) ? doctorsData : (doctorsData.doctors || doctorsData.data || []);

                    // If doctor, only fetch their own
                    if (isDoctor && currentUser) {
                        doctorsList = doctorsList.filter(d => d.user_id == currentUser.id);
                    }

                    // Fetch events for each doctor
                    const promises = doctorsList.map(doc =>
                        fetch(`${API_URL}/google/events?doctor_id=${doc.id}&start_date=${todayStr}&end_date=${todayStr}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        }).then(async res => {
                            if (res.ok) {
                                const data = await res.json();
                                return (data.events || []).map(ev => ({
                                    id: ev.id,
                                    start_time: ev.start?.dateTime ? new Date(ev.start.dateTime).toTimeString().slice(0, 8) : (ev.startTime || ''),
                                    end_time: ev.end?.dateTime ? new Date(ev.end.dateTime).toTimeString().slice(0, 8) : (ev.endTime || ''),
                                    patient_name: (ev.summary || ev.title || '').replace(/^CITA-\d+-\d+\s*/, '').replace(/\(.*?\)\s*/, '').trim() || 'Sin nombre',
                                    doctor_name: doc.name,
                                    doctor_id: doc.id,
                                    office: '',
                                    reason: ev.description ? ev.description.split('\n').find(l => l.startsWith('Motivo:'))?.replace('Motivo:', '').trim() : '',
                                    status: 'confirmed',
                                    google_event_id: ev.id,
                                    source: 'google'
                                }));
                            }
                            return [];
                        }).catch(() => [])
                    );

                    const results = await Promise.all(promises);
                    googleAppointments = results.flat();
                }
            } catch (e) {
                console.error('Error fetching Google events:', e);
            }

            // 3. Merge: prefer local records, add Google-only ones
            // Deduplicate by: google_event_id, local_XX ID match, or doctor+time match
            const localGoogleIds = new Set(localAppointments.map(a => a.google_event_id).filter(Boolean));
            const localIds = new Set(localAppointments.map(a => `local_${a.id}`));
            const localDoctorTimes = new Set(localAppointments.map(a => `${a.doctor_id}_${a.start_time}`));

            const uniqueGoogleAppts = googleAppointments.filter(g => {
                // Skip if already matched by google_event_id
                if (g.google_event_id && localGoogleIds.has(g.google_event_id)) return false;
                // Skip if it's a local_XX ID that matches a local appointment
                if (typeof g.id === 'string' && g.id.startsWith('local_') && localIds.has(g.id)) return false;
                // Skip if same doctor + same start time (likely the same appointment)
                const key = `${g.doctor_id}_${g.start_time}`;
                if (localDoctorTimes.has(key)) return false;
                return true;
            });

            const merged = [...localAppointments, ...uniqueGoogleAppts]
                .filter(a => (a.guest_name || '') !== 'Bloqueo de Agenda')
                .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

            setAppointments(merged);
        } catch (err) {
            console.error('Error fetching today appointments:', err);
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const map = {
            'scheduled': { label: 'Programada', color: '#083677', bg: '#eff6ff' },
            'confirmed': { label: 'Confirmada', color: '#8b5cf6', bg: '#f5f3ff' },
            'completed': { label: 'Completada', color: '#00BBBF', bg: '#ecfdf5' },
            'in_progress': { label: 'En curso', color: '#f59e0b', bg: '#fffbeb' },
            'no_show': { label: 'No asistió', color: '#ef4444', bg: '#fef2f2' }
        };
        const s = map[status] || { label: status, color: '#64748b', bg: '#f1f5f9' };
        return (
            <span style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: s.color,
                background: s.bg,
                whiteSpace: 'nowrap'
            }}>
                {s.label}
            </span>
        );
    };

    const formatTime = (time) => {
        if (!time) return '';
        const [h, m] = time.split(':');
        const hour = parseInt(h);
        const ampm = hour >= 12 ? 'p.m.' : 'a.m.';
        const h12 = hour % 12 || 12;
        return `${h12}:${m} ${ampm}`;
    };

    const today = new Date();
    const dateStr = today.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div className="dashboard-section">
            <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div className="dashboard-today-patients-header">
                    <div className="dashboard-today-title-wrapper">
                        <div className="dashboard-today-title">
                            <ClipboardList size={28} />
                            <h2>Pacientes del Día</h2>
                        </div>
                        <p>{dateStr}</p>
                    </div>
                    <div className="dashboard-today-count">
                        {loading ? '...' : appointments.length} cita{appointments.length !== 1 ? 's' : ''}
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '1rem 2rem 2rem' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                            Cargando citas de hoy...
                        </div>
                    ) : error ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>
                            {error}
                        </div>
                    ) : appointments.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                            <p style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}>📋</p>
                            <p style={{ margin: 0 }}>No hay citas programadas para hoy</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={{ padding: '12px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}>Hora</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}>Paciente</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}>Doctor</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}>Consultorio</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}>Motivo</th>
                                        <th style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {appointments.map((apt, idx) => (
                                        <tr
                                            key={apt.id}
                                            onClick={() => apt.patient_id && onPatientSelect(apt.patient_id)}
                                            style={{
                                                borderBottom: '1px solid #f1f5f9',
                                                cursor: apt.patient_id ? 'pointer' : 'default',
                                                transition: 'background 0.15s',
                                                background: idx % 2 === 0 ? '#ffffff' : '#f8fafc'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f8fafc'}
                                        >
                                            <td style={{ padding: '12px 8px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>
                                                {formatTime(apt.start_time)}
                                                {apt.end_time && <span style={{ color: '#94a3b8', fontWeight: 400 }}> - {formatTime(apt.end_time)}</span>}
                                            </td>
                                            <td style={{ padding: '12px 8px', fontWeight: 500, color: '#1e293b' }}>
                                                {apt.patient_name || 'Sin paciente asignado'}
                                            </td>
                                            <td style={{ padding: '12px 8px', color: '#475569' }}>
                                                {apt.doctor_name || '-'}
                                            </td>
                                            <td style={{ padding: '12px 8px', color: '#475569' }}>
                                                {apt.office || '-'}
                                            </td>
                                            <td style={{ padding: '12px 8px', color: '#475569', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {apt.reason || '-'}
                                            </td>
                                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                {getStatusBadge(apt.status)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ProfileView = ({ user }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Las contraseñas nuevas no coinciden.');
            return;
        }

        if (newPassword.length < 6) {
            setError('La nueva contraseña debe tener al menos 6 caracteres.');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/users/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword
                })
            });

            const data = await response.json();
            if (response.ok) {
                setMessage('Contraseña actualizada correctamente.');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setError(data.message || 'Error al actualizar contraseña.');
            }
        } catch (err) {
            setError('Error de conexión.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-section">
            <h2>Mi Perfil</h2>
            <div className="stat-card" style={{ marginBottom: '2rem' }}>
                <p><strong>Nombre:</strong> {user.name}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Rol:</strong> {user.role?.name || 'Usuario'}</p>
            </div>
            
            <div className="stat-card">
                <h3 style={{ marginBottom: '1rem' }}>Cambiar Contraseña</h3>
                {message && <div style={{ color: '#16a34a', marginBottom: '1rem', background: '#dcfce7', padding: '10px', borderRadius: '5px', border: '1px solid #bbf7d0', fontSize: '0.9rem' }}>{message}</div>}
                {error && <div style={{ color: '#dc2626', marginBottom: '1rem', background: '#fee2e2', padding: '10px', borderRadius: '5px', border: '1px solid #fecaca', fontSize: '0.9rem' }}>{error}</div>}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem', color: '#475569' }}>Contraseña Actual</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem', color: '#475569' }}>Nueva Contraseña</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={6}
                            style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem', color: '#475569' }}>Confirmar Nueva Contraseña</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }}
                        />
                    </div>
                    <button 
                        type="submit" 
                        disabled={loading}
                        style={{
                            padding: '12px',
                            background: '#083677',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            marginTop: '0.5rem'
                        }}
                    >
                        {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    // Restore activeView from localStorage or default to 'summary'
    const [activeView, setActiveView] = useState(() => {
        return localStorage.getItem('dashboard_activeView') || 'summary';
    });
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [billingPatient, setBillingPatient] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Define role-based permissions
    // Define role-based permissions (Keys MUST match normalized role names from DB)
    const rolePermissions = {
        'superadmin': ['all'],
        'admin': ['all'],
        'doctor': ['summary', 'profile', 'appointments', 'patients', 'patient-detail', 'new-patient', 'laboratory', 'nursing', 'shipments', 'samples', 'sample-appointments'],
        'medico': ['summary', 'profile', 'appointments', 'patients', 'patient-detail', 'new-patient', 'laboratory', 'nursing', 'shipments', 'samples', 'sample-appointments'], // Alias español
        'médico': ['summary', 'profile', 'appointments', 'patients', 'patient-detail', 'new-patient', 'laboratory', 'nursing', 'shipments', 'samples', 'sample-appointments'], // Alias con acento
        'nurse': ['summary', 'profile', 'patients', 'patient-detail', 'nursing', 'sample-appointments'],
        'enfermera': ['summary', 'profile', 'patients', 'patient-detail', 'nursing', 'sample-appointments'], // Alias
        'receptionist': ['summary', 'profile', 'appointments', 'appointments-manage', 'patients', 'patient-detail', 'new-patient', 'billing', 'billing-history', 'sample-appointments', 'samples'],
        'reception': ['summary', 'profile', 'appointments', 'appointments-manage', 'patients', 'patient-detail', 'new-patient', 'billing', 'billing-history', 'sample-appointments', 'samples'], // Alias
        'recepcion': ['summary', 'profile', 'appointments', 'appointments-manage', 'patients', 'patient-detail', 'new-patient', 'billing', 'billing-history', 'sample-appointments', 'samples'], // Alias
        'recepción': ['summary', 'profile', 'appointments', 'appointments-manage', 'patients', 'patient-detail', 'new-patient', 'billing', 'billing-history', 'sample-appointments', 'samples'], // Alias con acento
        'cashier': ['summary', 'profile', 'billing', 'billing-history'],
        'cobranza': ['summary', 'profile', 'billing', 'billing-history'], // Alias
        'lab tech': ['summary', 'profile', 'laboratory', 'shipments'],
        'laboratorio': ['summary', 'profile', 'laboratory', 'shipments'], // Alias
        'patient': ['profile', 'appointments'],
        'paciente': ['profile', 'appointments'] // Alias
    };

    const hasPermission = (view) => {
        if (!user || !user.role) return false;
        // Normalize role name to lowercase for comparison
        const roleName = (user.role.name || user.role).toLowerCase();
        const permissions = rolePermissions[roleName] || [];

        if (permissions.includes('all')) return true;
        return permissions.includes(view);
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        if (!token || !userData) {
            navigate('/login');
            return;
        }

        setUser(JSON.parse(userData));

        // Verify backend is reachable and token is still valid
        fetch(`${API_URL}/users`, {
            headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => {
            if (res.status === 401) {
                // Token expired or invalid
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('dashboard_activeView');
                navigate('/login');
            }
        }).catch(() => {
            // Backend unreachable — log out
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('dashboard_activeView');
            navigate('/login');
        });

        // Check URL parameters for view and google callback
        const urlParams = new URLSearchParams(window.location.search);
        const viewParam = urlParams.get('view');
        const googleConnected = urlParams.get('google_connected');
        const googleError = urlParams.get('google_error');
        const syncedCount = urlParams.get('synced_count');

        // If view parameter is set, switch to that view
        if (viewParam) {
            setActiveView(viewParam);
        }

        // Clean URL parameters if any relevant param is present
        if (viewParam || googleConnected || googleError) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Show success message if just connected to Google
        if (googleConnected === 'true') {
            if (syncedCount && parseInt(syncedCount) > 0) {
                setTimeout(() => {
                    alert(`✅ Conexión exitosa a Google Calendar.\n\nSe han sincronizado ${syncedCount} citas pendientes.`);
                }, 100);
            } else {
                setTimeout(() => {
                    alert('✅ Conexión exitosa a Google Calendar.');
                }, 100);
            }
        } else if (googleError) {
            setTimeout(() => {
                alert(`❌ Error al conectar con Google Calendar: ${googleError}`);
            }, 100);
        }
    }, [navigate]);

    // Persist activeView to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('dashboard_activeView', activeView);
    }, [activeView]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('dashboard_activeView'); // Clear saved view on logout
        navigate('/login');
    };

    // Helper to navigate and close sidebar on mobile
    const navigateTo = (view) => {
        setActiveView(view);
        setIsSidebarOpen(false);
    };

    if (!user) return <div>Cargando...</div>;

    const renderContent = () => {
        switch (activeView) {
            case 'patients':
                return (
                    <div className="dashboard-section">
                        <PatientsExpanded
                            onCreateNew={() => setActiveView('new-patient')}
                            onPatientSelect={(id) => {
                                setSelectedPatientId(id);
                                setActiveView('patient-detail');
                            }}
                            onPatientEdit={(id) => {
                                setSelectedPatientId(id);
                                setActiveView('edit-patient');
                            }}
                            onBilling={(patient) => {
                                setBillingPatient(patient);
                                setActiveView('billing');
                            }}
                        />
                    </div>
                );
            case 'edit-patient':
                return (
                    <div className="dashboard-section">
                        <PatientForm
                            patientId={selectedPatientId}
                            onCancel={() => setActiveView('patients')}
                            onSuccess={() => setActiveView('patients')}
                        />
                    </div>
                );
            case 'new-patient':
                return (
                    <div className="dashboard-section">
                        <PatientForm
                            onCancel={() => setActiveView('patients')}
                            onSuccess={() => setActiveView('patients')}
                        />
                    </div>
                );
            case 'patient-detail':
                return (
                    <div className="dashboard-section">
                        <PatientDetail
                            patientId={selectedPatientId}
                            onBack={() => setActiveView('patients')}
                        />
                    </div>
                );
            case 'profile':
                return <ProfileView user={user} />;
            case 'appointments':
                return (
                    <div className="dashboard-section">
                        <GoogleCalendarView />
                    </div>
                );
            case 'appointments-manage':
                return (
                    <div className="dashboard-section">
                        <AppointmentsManager />
                    </div>
                );
            case 'appointments-report':
                return (
                    <div className="dashboard-section">
                        <h2>Reporte de Citas</h2>
                        <p>Reportes y estadísticas de citas médicas.</p>
                    </div>
                );
            case 'reports':
                return <ReportsHub />;
            case 'catalogs':
                return <Catalogs />;
            case 'site-editor':
                return (
                    <div className="dashboard-section">
                        <SiteEditor />
                    </div>
                );
            case 'settings':
                return (
                    <div className="dashboard-section">
                        <SettingsPage />
                    </div>
                );
            case 'billing':
                return <BillingTabs />;
            case 'samples':
                return <SamplesHub />;
            case 'users':
                return <UserManager />;
            case 'sample-appointments':
                return <SampleAppointments />;
            default:
                return <TodayPatients
                    onPatientSelect={(id) => {
                        setSelectedPatientId(id);
                        setActiveView('patient-detail');
                    }}
                />;
        }
    };

    return (
        <div className="dashboard-layout">
            {/* Mobile Menu Toggle - Outside sidebar so it's always visible */}
            <button
                className={`mobile-menu-toggle ${isSidebarOpen ? 'open' : ''}`}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
                {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Overlay for mobile */}
            {isSidebarOpen && (
                <div
                    className="sidebar-overlay visible"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo-container" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} title="Ir a la página principal">
                        <img
                            src="/logo.png"
                            alt="GeneCenter"
                            className="sidebar-logo"
                        />
                    </div>
                    <p>{user.name}</p>
                    <span className="role-badge">{user.role?.name || 'Usuario'}</span>
                </div>

                <nav className="sidebar-nav">
                    {/* 1. Dashboard */}
                    {hasPermission('summary') && (
                        <button
                            onClick={() => navigateTo('summary')}
                            className={activeView === 'summary' ? 'active' : ''}
                        >
                            <Home size={18} /> Dashboard
                        </button>
                    )}

                    {/* 2. Mis Citas */}
                    {hasPermission('appointments') && (
                        <button
                            onClick={() => navigateTo('appointments')}
                            className={activeView === 'appointments' ? 'active' : ''}
                        >
                            <Calendar size={18} /> Mis Citas
                        </button>
                    )}

                    {/* 3. Pacientes */}
                    {hasPermission('patients') && (
                        <button
                            onClick={() => navigateTo('patients')}
                            className={activeView === 'patients' ? 'active' : ''}
                        >
                            <Users size={18} /> Pacientes
                        </button>
                    )}

                    {/* 4. Cobranza */}
                    {hasPermission('billing') && (
                        <button
                            onClick={() => { setBillingPatient(null); navigateTo('billing'); }}
                            className={activeView === 'billing' ? 'active' : ''}
                        >
                            <DollarSign size={18} /> Cobranza
                        </button>
                    )}

                    {/* 5. Estudios */}
                    {(hasPermission('nursing') || hasPermission('laboratory') || hasPermission('shipments') || hasPermission('samples')) && (
                        <button
                            onClick={() => navigateTo('samples')}
                            className={activeView === 'samples' ? 'active' : ''}
                        >
                            <FlaskConical size={18} /> Estudios
                        </button>
                    )}

                    {/* Citas de Muestras */}
                    {hasPermission('sample-appointments') && (
                        <button
                            onClick={() => navigateTo('sample-appointments')}
                            className={activeView === 'sample-appointments' ? 'active' : ''}
                        >
                            <Syringe size={18} /> Citas de Muestras
                        </button>
                    )}

                    {/* 6. Reportes */}
                    {hasPermission('reports') && (
                        <button
                            onClick={() => navigateTo('reports')}
                            className={activeView === 'reports' ? 'active' : ''}
                        >
                            <BarChart3 size={18} /> Reportes
                        </button>
                    )}

                    {/* 7. Catálogos */}
                    {hasPermission('catalogs') && (
                        <button
                            onClick={() => navigateTo('catalogs')}
                            className={activeView === 'catalogs' ? 'active' : ''}
                        >
                            <Package size={18} /> Catálogos
                        </button>
                    )}

                    {/* 7. Editar Sitio */}
                    {hasPermission('site-editor') && (
                        <button
                            onClick={() => navigateTo('site-editor')}
                            className={activeView === 'site-editor' ? 'active' : ''}
                        >
                            <Globe size={18} /> Editar Sitio
                        </button>
                    )}

                    {/* 8. Usuarios */}
                    {hasPermission('users') && (
                        <button
                            onClick={() => navigateTo('users')}
                            className={activeView === 'users' ? 'active' : ''}
                        >
                            <Users size={18} /> Usuarios
                        </button>
                    )}

                    {/* 9. Admin (Profile) */}
                    <button
                        onClick={() => navigateTo('profile')}
                        className={activeView === 'profile' ? 'active' : ''}
                    >
                        <User size={18} /> {user.name}
                    </button>

                    {/* 10. Configuración */}
                    {hasPermission('settings') && (
                        <button
                            onClick={() => navigateTo('settings')}
                            className={activeView === 'settings' ? 'active' : ''}
                        >
                            <Settings size={18} /> Configuración
                        </button>
                    )}

                    <button onClick={handleLogout} className="logout-btn">
                        <LogOut size={18} /> Salir
                    </button>
                </nav>
            </aside>

            <main className="dashboard-content">
                {renderContent()}
            </main>
        </div>
    );
};

export default Dashboard;
